import { Author } from '../../api/Author';
import { Recipe } from '../../api/Recipe';
import { NavigateFunction } from 'react-router-dom';
import { deriveEditRecipePageState } from '../../domains/recipes/use-cases/recipePageActions';
import useRecipePageActions from './useRecipePageActions';

type ToastType = 'success' | 'error' | 'info';

interface UseEditRecipePageControllerOptions {
  recipe: Recipe | null;
  recipeId: string | undefined;
  author: Author | null;
  reloadRecipe: (errorPrefix?: string) => Promise<Recipe | null>;
  navigate: NavigateFunction;
  setEditing: (value: boolean) => void;
  toggleEdit: () => void;
  isFavorite: (recipeId: string) => boolean;
  addFavorite: (recipe: Recipe) => Promise<void>;
  removeFavorite: (recipeId: string) => Promise<void>;
  showToast: (message: string, type?: ToastType) => void;
}

interface UseEditRecipePageControllerResult {
  isAuthor: boolean;
  isRecipeFavorite: boolean;
  handleToggleEdit: () => void;
  handleShare: () => Promise<void>;
  handleRecipeUpdatedInForm: () => Promise<void>;
  handleRecipeDeletedInForm: () => void;
  handleFavoriteToggle: () => Promise<void>;
}

export const useEditRecipePageController = ({
  recipe,
  recipeId,
  author,
  reloadRecipe,
  navigate,
  setEditing,
  toggleEdit,
  isFavorite,
  addFavorite,
  removeFavorite,
  showToast,
}: UseEditRecipePageControllerOptions): UseEditRecipePageControllerResult => {
  const pageState = deriveEditRecipePageState(author, recipe, isFavorite);
  const {
    handleShare,
    handleRecipeUpdatedInForm,
    handleRecipeDeletedInForm,
    handleFavoriteToggle,
  } = useRecipePageActions({
    recipe,
    recipeId,
    reloadRecipe,
    navigate,
    setEditing,
    isFavorite,
    addFavorite,
    removeFavorite,
    showToast,
  });

  return {
    isAuthor: pageState.isAuthor,
    isRecipeFavorite: pageState.isRecipeFavorite,
    handleToggleEdit: toggleEdit,
    handleShare,
    handleRecipeUpdatedInForm,
    handleRecipeDeletedInForm,
    handleFavoriteToggle,
  };
};

export default useEditRecipePageController;
